import React, { useCallback, useState } from "react";
import { usePageViewPreferences } from "../shared/hooks/usePageViewPreferences";

export const PAGE_VIEW_PREFERENCE_LABEL_SAVE = "Save Current View";
export const PAGE_VIEW_PREFERENCE_LABEL_RESET = "Reset / Clear View";
const LABEL_SAVE_SHORT = "Save";
const LABEL_RESET_SHORT = "Reset";
const LABEL_SAVING = "Saving...";
const LABEL_CLEARING = "Resetting...";
const TITLE_SAVE =
  "Save current view (list/grid/calendar), filters, and date range as your default for this page";
const TITLE_RESET =
  "Clear your saved default view and restore the standard filters for this page";
const TITLE_SAVE_SIGNED_OUT = "Sign in to save your current view";
const TITLE_RESET_SIGNED_OUT = "Sign in to reset your view";

/**
 * Reusable control: one toolbar toggle with two direct modes.
 * - Save Current View — persist active filters and layout as this user's default.
 * - Reset / Clear View — revert filters to the original default (and remove a saved default).
 *
 * @param {{
 *   pageKey: string,
 *   accountId?: string,
 *   userId?: string,
 *   enabled?: boolean,
 *   shouldSkipLoad?: () => boolean,
 *   collectState: () => object,
 *   applyState: (state: object) => void,
 *   onAfterClearLocalState?: () => void,
 *   onError?: (message: string) => void,
 *   onClearConfirm?: (proceed: () => void) => void,
 *   saveLabel?: string,
 *   resetLabel?: string,
 *   savingLabel?: string,
 *   clearingLabel?: string,
 *   preferencesControl?: ReturnType<typeof usePageViewPreferences>,
 * }} props
 */
export default function PageViewPreferenceSaveButton({
  pageKey,
  accountId,
  userId,
  enabled = true,
  shouldSkipLoad,
  collectState,
  applyState,
  onAfterClearLocalState,
  onError,
  onClearConfirm,
  saveLabel = PAGE_VIEW_PREFERENCE_LABEL_SAVE,
  resetLabel = PAGE_VIEW_PREFERENCE_LABEL_RESET,
  savingLabel = LABEL_SAVING,
  clearingLabel = LABEL_CLEARING,
  preferencesControl,
}) {
  const internalPreferences = usePageViewPreferences({
    pageKey,
    accountId,
    userId,
    enabled: preferencesControl ? false : enabled,
    shouldSkipLoad,
    collectState,
    applyState,
  });
  const { saving, clearing, hasSavedDefault, saveAsDefault, clearDefault } =
    preferencesControl || internalPreferences;
  const [localError, setLocalError] = useState("");

  const signedOut = !accountId || !userId;
  const busy = saving || clearing;
  const mode = hasSavedDefault ? "save" : "reset";

  const saveAriaLabel = saving ? savingLabel : saveLabel;
  const resetAriaLabel = clearing ? clearingLabel : resetLabel;

  const handleSaveClick = useCallback(async () => {
    if (busy || signedOut) return;
    setLocalError("");
    try {
      await saveAsDefault();
    } catch (err) {
      const msg = err && err.message ? err.message : "Failed to save default view.";
      setLocalError(msg);
      if (typeof onError === "function") onError(msg);
    }
  }, [saveAsDefault, onError, busy, signedOut]);

  const applyLocalReset = useCallback(() => {
    if (typeof onAfterClearLocalState === "function") {
      onAfterClearLocalState();
    }
  }, [onAfterClearLocalState]);

  const runClear = useCallback(async () => {
    if (busy || signedOut) return;
    setLocalError("");
    try {
      await clearDefault();
      applyLocalReset();
    } catch (err) {
      const msg = err && err.message ? err.message : "Failed to reset default view.";
      setLocalError(msg);
      if (typeof onError === "function") onError(msg);
    }
  }, [clearDefault, applyLocalReset, onError, busy, signedOut]);

  const handleResetClick = useCallback(() => {
    if (busy || signedOut) return;
    if (!hasSavedDefault) {
      setLocalError("");
      applyLocalReset();
      return;
    }
    if (typeof onClearConfirm === "function") {
      onClearConfirm(runClear);
      return;
    }
    runClear();
  }, [hasSavedDefault, onClearConfirm, runClear, applyLocalReset, busy, signedOut]);

  const saveBtnClass = [
    "page-view-preference-toggle__btn",
    "page-view-preference-toggle__btn--save",
    mode === "save" ? "page-view-preference-toggle__btn--active" : "",
    saving ? "page-view-preference-toggle__btn--busy" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const resetBtnClass = [
    "page-view-preference-toggle__btn",
    "page-view-preference-toggle__btn--reset",
    mode === "reset" ? "page-view-preference-toggle__btn--active" : "",
    clearing ? "page-view-preference-toggle__btn--busy" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className="page-view-preference-save-wrap">
      <div
        role="group"
        aria-label="Default view"
        className="page-view-preference-toggle toolbar-control"
      >
        <button
          type="button"
          className={saveBtnClass}
          onClick={handleSaveClick}
          disabled={signedOut || busy}
          title={signedOut ? TITLE_SAVE_SIGNED_OUT : TITLE_SAVE}
          aria-label={saveAriaLabel}
          aria-pressed={mode === "save"}
          aria-busy={saving}
        >
          {saving ? (
            <span className="page-view-preference-toggle__spinner" aria-hidden="true" />
          ) : (
            <span className="page-view-preference-toggle__label">{LABEL_SAVE_SHORT}</span>
          )}
        </button>
        <button
          type="button"
          className={resetBtnClass}
          onClick={handleResetClick}
          disabled={signedOut || busy}
          title={signedOut ? TITLE_RESET_SIGNED_OUT : TITLE_RESET}
          aria-label={resetAriaLabel}
          aria-pressed={mode === "reset"}
          aria-busy={clearing}
        >
          {clearing ? (
            <span className="page-view-preference-toggle__spinner" aria-hidden="true" />
          ) : (
            <span className="page-view-preference-toggle__label">{LABEL_RESET_SHORT}</span>
          )}
        </button>
      </div>
      {localError ? (
        <span className="text-danger page-view-preference-save-wrap__error" role="alert">
          {localError}
        </span>
      ) : null}
    </span>
  );
}
